import { DEFAULT_CATEGORIES } from "./data";

let request: IDBOpenDBRequest;
let version = 2;
const DB_NAME = 'expensesDB';

export enum Stores {
  Expenses = 'expenses',
  Categories = 'categories',
  Settings = 'settings',
  ExchangeRates = 'exchange_rates',
}

export const initDB = (): Promise<boolean|IDBDatabase> => {
  return new Promise((resolve) => {
    request = indexedDB.open(DB_NAME, version);

    // if the data object store doesn't exist, create it
    request.onupgradeneeded = () => {
      var db = request.result;

      if (!db.objectStoreNames.contains(Stores.Expenses)) {
        console.log('Creating expenses store');
        db.createObjectStore(Stores.Expenses, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(Stores.Categories)) {
        console.log('Creating categories store');
        db.createObjectStore(Stores.Categories, { keyPath: 'id' });
        DEFAULT_CATEGORIES.forEach(category => addData(Stores.Categories, category));
      }
      if (!db.objectStoreNames.contains(Stores.Settings)) {
        console.log('Creating settings store');
        db.createObjectStore(Stores.Settings, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(Stores.ExchangeRates)) {
        console.log('Creating exchange_rates store');
        db.createObjectStore(Stores.ExchangeRates, { keyPath: 'id' });
      }
      // no need to resolve here
    };

    request.onsuccess = (event) => {
      var db = (event.target as IDBOpenDBRequest).result;
      // get current version and store it
      version = db.version;
      resolve(db);
    };

    request.onerror = (_e) => {
      resolve(false);
    };
  });
};

export const addData = <T>(storeName: string, data: T): Promise<T|string|null> => {
  return new Promise((resolve) => {
    request = indexedDB.open(DB_NAME, version);

    request.onsuccess = (event) => {
      var db = (event.target as IDBOpenDBRequest).result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.add(data);
      resolve(data);
    };

    request.onerror = () => {
      const error = request.error?.message
      if (error) {
        resolve(error);
      } else {
        resolve('Unknown error');
      }
    };
  });
};

export const deleteData = (storeName: string, key: string): Promise<boolean> => {
  return new Promise((resolve) => {
    request = indexedDB.open(DB_NAME, version);

    request.onsuccess = (event) => {
      let db = (event.target as IDBOpenDBRequest).result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const res = store.delete(key);
      res.onsuccess = () => {
        resolve(true);
      };
      res.onerror = () => {
        resolve(false);
      }
    };
  });
};

export const clearStore = (storeName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    request = indexedDB.open(DB_NAME, version);

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    };

    request.onerror = () => reject(request.error);
  });
};

export const updateData = <T>(storeName: string, key: string, data: T): Promise<T|string|null> => {
  return new Promise((resolve) => {
    request = indexedDB.open(DB_NAME, version);

    request.onsuccess = (event) => {
      var db = (event.target as IDBOpenDBRequest).result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const res = store.get(key);
      res.onsuccess = () => {
        const newData = { ...res.result, ...data };
        store.put(newData);
        resolve(newData);
      };
      res.onerror = () => {
        resolve(null);
      }
    };
  });
};

export const putData = <T extends { id: string }>(storeName: string, data: T): Promise<T|string|null> => {
  return new Promise((resolve) => {
    request = indexedDB.open(DB_NAME, version);

    request.onsuccess = (event) => {
      var db = (event.target as IDBOpenDBRequest).result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.put(data);
      resolve(data);
    };

    request.onerror = () => {
      const error = request.error?.message;
      if (error) {
        resolve(error);
      } else {
        resolve('Unknown error');
      }
    };
  });
};

export const getStoreData = <T>(storeName: Stores): Promise<T[]> => {
  return new Promise((resolve) => {
    request = indexedDB.open(DB_NAME);

    request.onsuccess = (event) => {
      var db = (event.target as IDBOpenDBRequest).result;
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const res = store.getAll();
      res.onsuccess = () => {
        resolve(res.result);
      };
    };
  });
};

export const getStoreDataById = <T>(storeName: Stores, id: string): Promise<T|undefined> => {
    return new Promise((resolve) => {
      request = indexedDB.open(DB_NAME);

      request.onsuccess = (event) => {
        var db = (event.target as IDBOpenDBRequest).result;
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const res = store.get(id);
        res.onsuccess = () => {
          resolve(res.result);
        };
      };
    });
  };


export {};
